import moment from 'moment'

/**
 * Floors the selectable delivery time at a minimum instant (for unconfirmed UE/CC orders, the
 * rescheduled `variationCodeAdjustedTime`). Mirrors the OnSite behavior: a customer can pick the
 * adjusted time or later, but never an earlier slot/time on the same day.
 *
 * All comparisons deliberately mirror how the pickers build a selected instant from a slot string
 * (`moment(selectedDate).hours(h).minutes(m)`), so a slot that maps to the minimum is kept and only
 * strictly-earlier slots are dropped. It is day-aware for free: when `selectedDate` is a later day,
 * the slot instant is after the minimum and nothing is filtered.
 */

const parseHoursMinutes = (time: string): {hours: number; minutes: number} | undefined => {
  const parts = time.split(':')
  if (parts.length < 2) return undefined
  const [hours, minutes] = parts.map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return undefined
  }
  return {hours, minutes}
}

/**
 * True when the given "HH:mm" slot on the selected date falls strictly before `minimumDateTime`.
 * Returns false (keep the slot) when no minimum/date is provided or the slot is unparseable.
 */
export const isSlotBeforeMinimum = (
  selectedDate: string | undefined,
  slot: string | undefined,
  minimumDateTime?: string
): boolean => {
  if (!minimumDateTime || !selectedDate || !slot) {
    return false
  }
  const parsed = parseHoursMinutes(slot)
  if (!parsed) {
    return false
  }
  const slotMoment = moment(selectedDate)
    .hours(parsed.hours)
    .minutes(parsed.minutes)
    .seconds(0)
    .milliseconds(0)
  return slotMoment.isBefore(moment(minimumDateTime))
}
